import { Injectable } from '@angular/core';
import { environment } from '../../environments/environment';
import { UserService } from './user/user.service';
import { SessionService } from './session.service';
import { AnalyticsBrowser } from '@segment/analytics-next'
import { objectPropertiesToArray } from '@rallycommerce/common/utils.js';
import { CookieStorage } from './data/data-storage';
import * as CryptoJS from 'crypto-js';

declare const fbq: any;
declare const ttq: any;
declare const gtag: any;
declare const uetq: any;
declare const oaiq: any;
declare const rewardful: any;
declare const Rewardful: any;
declare global {
  interface Window {
    fbq: any;
    gtag: any;
    ttq: any;
    oaiq: any;
    analytics: any;
    dataLayer: any[];
    tolt_referral: string;
    rewardful: any;
    Rewardful: any;
    clarity: any;
    Intercom: any;
  }
}

@Injectable({
  providedIn: 'root'
})

export class InternalAnalyticsService {
  private static readonly SIGNUP_VALUE = 195;
  private static readonly CARD_ADDED_VALUE = 520;
  private static readonly SEGMENT_WRITE_KEY = 'vfRBtbAIABlnLFiqstdoJguAmMrWPnFU';

  private analytics: AnalyticsBrowser | null = null;

  isUserIdentified = false;
  constructor(private user: UserService, private session: SessionService) {
    this.initializeAnalytics();
  }

  /**
   * Lazily load the Segment SDK. Called only once a real, authenticated user is
   * identified so that anonymous onboarding traffic never counts against our
   * Segment MTU quota. Safe to call multiple times — no-ops after the first load.
   */
  private loadSegment(): void {
    if (this.analytics) {
      return;
    }

    try {
      this.analytics = AnalyticsBrowser.load({ writeKey: InternalAnalyticsService.SEGMENT_WRITE_KEY });
      this.hideIntercomLauncherOnMobile();
    } catch (error) {
      console.warn('Failed to load Segment', error);
    }
  }

  /**
   * Intercom is booted as a Segment destination, so it only becomes available
   * once Segment finishes loading its destinations. Once ready, hide the default
   * launcher bubble on mobile (custom launcher triggers still open the Messenger).
   * Wrapped defensively so a missing/late Intercom never breaks analytics init.
   */
  private hideIntercomLauncherOnMobile(): void {
    try {
      this.analytics?.ready(() => {
        try {
          if (this.session.isMobileVersion) {
            window.Intercom?.('update', { hide_default_launcher: true });
          }
        } catch (error) {
          console.warn('Failed to hide Intercom launcher', error);
        }
      });
    } catch (error) {
      console.warn('Failed to register Intercom launcher handler', error);
    }
  }

  get isProductionEnv(): boolean {
    return environment.name === 'production';
  }

  get userId() {
    return this.user?.id || this.userExternalId || '';
  }

  get toltReferral(): string {
    return window['tolt_referral'] || localStorage.getItem('tolt_referral') || '';
  }

  get rewardfulReferralId(): string {
    try {
      return window.Rewardful?.referral || localStorage.getItem('rewardful_referral_id') || '';
    } catch {
      return localStorage.getItem('rewardful_referral_id') || '';
    }
  }

  get userExternalId(): string {
    const queryParams = new URLSearchParams(window.location.search);
    return localStorage.getItem('userExternalId') || queryParams.get('uuid') || '';
  }

  get coupon(): string {
    const queryParams = new URLSearchParams(window.location.search);
    return localStorage.getItem('signupCouponCode') || '';
  }

  get referral(): string {
    const queryParams = new URLSearchParams(window.location.search);
    return localStorage.getItem('rrf') || queryParams.get('rrf') || '';
  }

  get campaign(): string {
    const queryParams = new URLSearchParams(window.location.search);
    return localStorage.getItem('rcmp') || queryParams.get('rcmp') || '';;
  }

  get utmSource(): string {
    const queryParams = new URLSearchParams(window.location.search);
    return localStorage.getItem('utm_source') || queryParams.get('utm_source') || '';
  }

  get utmMedium(): string {
    const queryParams = new URLSearchParams(window.location.search);
    return localStorage.getItem('utm_medium') || queryParams.get('utm_medium') || '';
  }

  get utmCampaign(): string {
    const queryParams = new URLSearchParams(window.location.search);
    return localStorage.getItem('utm_campaign') || queryParams.get('utm_campaign') || '';
  }

  get utmTerm(): string {
    const queryParams = new URLSearchParams(window.location.search);
    return localStorage.getItem('utm_term') || queryParams.get('utm_term') || '';
  }

  get utmContent(): string {
    const queryParams = new URLSearchParams(window.location.search);
    return localStorage.getItem('utm_content') || queryParams.get('utm_content') || '';
  }

  get referrer(): string {
    const queryParams = new URLSearchParams(window.location.search);
    return localStorage.getItem('referrer') || queryParams.get('referrer') || '';
  }

  get msclkid(): string {
    const queryParams = new URLSearchParams(window.location.search);
    return localStorage.getItem('msclkid') || queryParams.get('msclkid') || '';
  }

  get oppref(): string {
    const queryParams = new URLSearchParams(window.location.search);
    const cookieOppref = new CookieStorage().getItem('__oppref');
    return localStorage.getItem('oppref') || queryParams.get('oppref') || cookieOppref || '';
  }

  get secondaryReferrer(): string {
    return localStorage.getItem('secondary_referrer') || '';
  }

  initializeAnalytics() {
    if (this.isProductionEnv) {
      this.initializeFacebookPixel();
      this.initializeGA();
      this.initializeTikTok();
      this.initializeBingUET();
      this.initializeBingIdSync();
      this.initializeOpenAiPixel();
      this.identify();
      this.initializeClarity();
    }

    this.handleMeta();
  }

  identify() {
    try {
      if (this.isProductionEnv && this.user.id && !this.isUserIdentified) {
        this.identifyClarity();
        this.loadSegment();
        if (!this.analytics) return;
        this.analytics.identify(this.userId, {
          name: this.user?.name,
          email: this.user?.email,
          plan: this.user.subscription?.planName,
          // The signup split-test arm this user registered under, so downstream
          // funnels can be cut by variant. Absent for accounts created before the
          // test — sent as undefined, which Segment drops.
          signupVariant: this.user.userData?.signupVariant
        });
        this.isUserIdentified = true;
      }
    } catch (error) {
      console.log(error);
    }
  }

  reset() {
    if (this.isProductionEnv) {
      this.analytics?.reset();
      this.analytics?.identify();
    }

  }

  trackPage(pageTitle: string | AnalyticsPage) {
    if (!this.rewardfulReferralId && !this.user.hasCreditCardOnFile) {
      this.initializeRewardful();
    }
    try {
      if (this.isProductionEnv) {
        if (!this.user.hasCreditCardOnFile) {
          if (!this.toltReferral) {
            this.initializeToltReferral();
          }
          // if (!this.rewardfulReferralId && !this.user.hasCreditCardOnFile) {
          //   this.initializeRewardful();
          // }
        }
        if (this.user?.id) {
          this.loadSegment();
          this.analytics?.page({
            userId: this.userId,
            name: pageTitle
          });
        }
        this.handleFacebookPixelPageView(pageTitle as AnalyticsPage);
        this.handleTikTokPageView(pageTitle as AnalyticsPage);
        this.handleGAPageView(pageTitle as AnalyticsPage);
        this.handleOpenAiPageView(pageTitle as AnalyticsPage);
      }
    } catch (error) {
      console.log(error);
    }
  }

  trackEvent(eventName: AnalyticsEvent, eventDetails: any = {}) {
    try {
      if (!this.isProductionEnv || !eventName || !this.user?.id) return;
      this.loadSegment();
      if (!this.analytics) return;
      eventDetails.userId = this.userId;
      this.analytics.track(eventName, eventDetails, this.userId as any);
    } catch (error) {
      console.log(error);
    }
  }

  fireFirstTimeUserVisit(isSocialLogin = false) {
    try {
      if (this.isProductionEnv) {
        const eventId = this.generateCompleteRegistrationEventId();
        const value = InternalAnalyticsService.SIGNUP_VALUE;
        fbq('track', 'CompleteRegistration', {
          value,
          currency: 'USD',
          ...(eventId && { event_id: eventId })
        });
        ttq.identify({ "email": this.user?.email, "external_id": this.userId });
        ttq.track('CompleteRegistration', { "contents": [{ "content_id": "rosie" }] });
        gtag("event", "sign_up", { send_to: ['AW-11564387623', 'G-8XN13HV4CR'], value, currency: 'USD' });
        gtag('event', 'conversion_event_signup', { send_to: ['AW-11564387623', 'G-8XN13HV4CR'], value, currency: 'USD' });
        uetq.push('event', 'sign_up', {
          event_category: 'registration',
          revenue_value: value,
          currency: 'USD'
        });
        this.fireOpenAi('registration_completed', {
          type: 'customer_action',
          amount: value * 100,
          currency: 'USD'
        }, {
          event_id: this.generateEventId(this.userId, 'registration_completed')
        });
      }
    } catch (error) {
      console.log(error);
    }
  }

  // Lead conversion for flows without the agent-preview page (Variants C/D), where
  // handleFacebookPixelPageView would normally fire it. Same pixels as that path.
  // The event_id is deterministic (lead uuid, falling back to the user external
  // id for the lead-less Variants C/D), so Facebook dedupes repeat sends.
  fireLeadEvent() {
    try {
      if (this.isProductionEnv) {
        const eventId = this.generateLeadEventId();
        fbq('track', 'Lead', {
          ...(eventId && { event_id: eventId })
        });
        uetq.push('event', 'lead', {
          event_category: 'onboarding'
        });
      }
    } catch (error) {
      console.log(error);
    }
  }

  fireLaunchPageViewed() {
    try {
      if (this.isProductionEnv) {
        const userUuid = this.user?.id || '';
        const eventId = this.generateEventId(userUuid, 'LaunchPageViewed');
        fbq('track', 'LaunchPageViewed', {
          ...(eventId && { event_id: eventId })
        });
        gtag('event', 'launch_page_viewed', { send_to: ['AW-11564387623', 'G-8XN13HV4CR'] });
        gtag("event", "launch_page_viewed_yt", { send_to: ['AW-11564387623', 'G-8XN13HV4CR'] }); // YT
        uetq.push('event', 'launch_page_viewed', {
          event_category: 'onboarding'
        });
      }
    } catch (error) {
      console.log(error);
    }
  }

  fireCreditCardAddedEvent() {
    try {
      if (this.isProductionEnv) {
        const eventId = this.generateEventId(this.userId, 'ConversionEventCA');
        const value = InternalAnalyticsService.CARD_ADDED_VALUE;
        fbq('track', 'CardAdded', {
          value,
          currency: 'USD',
          ...(eventId && { event_id: eventId })
        });
        ttq.track('CardAdded');
        gtag("event", "add_payment_info", { send_to: ['AW-11564387623', 'G-8XN13HV4CR'], value, currency: 'USD' });
        uetq.push('event', 'add_payment_info', {
          event_category: 'billing',
          revenue_value: value,
          currency: 'USD'
        });
        this.fireOpenAi('custom', {
          type: 'custom',
          amount: value * 100,
          currency: 'USD'
        }, {
          custom_event_name: 'card_added',
          event_id: this.generateEventId(this.userId, 'card_added')
        });

        // Note: Rewardful conversion tracking is handled by backend via lead metadata
      }
    } catch (error) {
      console.warn('Credit card added tracking error:', error);
    }
  }

  fireInitialBusinessScrapeFinished() {
    try {
      if (this.isProductionEnv) {
        const eventId = this.generateEventId(this.userId, 'BusinessScrapeFinished');
        fbq('track', 'BusinessScrapeFinished', {
          ...(eventId && { event_id: eventId })
        });
        ttq.track('BusinessScrapeFinished');
        gtag("event", "business_scrape_finished", { send_to: ['AW-11564387623', 'G-8XN13HV4CR'] });
        gtag("event", "business_scrape_finished_yt", { send_to: ['AW-11564387623', 'G-8XN13HV4CR'] });
        uetq.push('event', 'business_scrape_finished', {
          event_category: 'onboarding'
        });
      }
    } catch (error) {
      console.warn('Business scrape finished tracking error:', error);
    }
  }

  private initializeFacebookPixel() {
    const facebookPixelEl = document.createElement('script');
    const facebookPixelNoScriptEl = document.createElement('noscript');
    facebookPixelEl.type = 'text/javascript';
    facebookPixelEl.innerHTML = `
      !function(f,b,e,v,n,t,s)
      {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
      n.callMethod.apply(n,arguments):n.queue.push(arguments)};
      if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
      n.queue=[];t=b.createElement(e);t.async=!0;
      t.src=v;s=b.getElementsByTagName(e)[0];
      s.parentNode.insertBefore(t,s)}(window, document,'script',
      'https://connect.facebook.net/en_US/fbevents.js');
      fbq('init', '26196183753362886');
      fbq('track', 'PageView');
  `;
    facebookPixelNoScriptEl.innerHTML = `<img height="1" width="1" style="display:none"
      src="https://www.facebook.com/tr?id=891907839484716&ev=PageView&noscript=1"/>`
    document.body.appendChild(facebookPixelEl);
    document.body.appendChild(facebookPixelNoScriptEl);
  }

  private initializeGA() {
    const GAInitializeEl = document.createElement('script');
    GAInitializeEl.async = true;
    GAInitializeEl.src = 'https://www.googletagmanager.com/gtag/js?id=G-8XN13HV4CR';
    const GAInitializeAWEl = document.createElement('script');
    GAInitializeAWEl.async = true;
    GAInitializeAWEl.src = 'https://www.googletagmanager.com/gtag/js?id=AW-11564387623';
    const GAEl = document.createElement('script');
    GAEl.type = 'text/javascript';
    GAEl.innerHTML = `
        window.dataLayer = window.dataLayer || [];
        function gtag(){dataLayer.push(arguments);}
        gtag('js', new Date());
        gtag('config', 'G-8XN13HV4CR');
        gtag('config', 'AW-11564387623');
  `;
    document.body.appendChild(GAInitializeEl);
    document.body.appendChild(GAInitializeAWEl);
    document.body.appendChild(GAEl);
  }

  private initializeTikTok() {
    const tikTokEl = document.createElement('script');
    tikTokEl.type = 'text/javascript';
    tikTokEl.innerHTML = `
        !function (w, d, t) {
          w.TiktokAnalyticsObject=t;var ttq=w[t]=w[t]||[];ttq.methods=["page","track","identify","instances","debug","on","off","once","ready","alias","group","enableCookie","disableCookie","holdConsent","revokeConsent","grantConsent"],ttq.setAndDefer=function(t,e){t[e]=function(){t.push([e].concat(Array.prototype.slice.call(arguments,0)))}};for(var i=0;i<ttq.methods.length;i++)ttq.setAndDefer(ttq,ttq.methods[i]);ttq.instance=function(t){for(
        var e=ttq._i[t]||[],n=0;n<ttq.methods.length;n++)ttq.setAndDefer(e,ttq.methods[n]);return e},ttq.load=function(e,n){var r="https://analytics.tiktok.com/i18n/pixel/events.js",o=n&&n.partner;ttq._i=ttq._i||{},ttq._i[e]=[],ttq._i[e]._u=r,ttq._t=ttq._t||{},ttq._t[e]=+new Date,ttq._o=ttq._o||{},ttq._o[e]=n||{};n=document.createElement("script")
        ;n.type="text/javascript",n.async=!0,n.src=r+"?sdkid="+e+"&lib="+t;e=document.getElementsByTagName("script")[0];e.parentNode.insertBefore(n,e)};
          ttq.load('CRC9E7RC77UBSLKS3BEG');
          ttq.page();
        }(window, document, 'ttq');
  `;
    document.body.appendChild(tikTokEl);
  }

  private initializeBingUET() {
    if ((window as any).__bingUETInitialized) {
      return;
    }
    (window as any).__bingUETInitialized = true;

    const bingUETEl = document.createElement('script');
    bingUETEl.type = 'text/javascript';
    bingUETEl.innerHTML = `
        (function(w,d,t,u,o){w[u]=w[u]||[],o.ts=(new Date).getTime();var n=d.createElement(t);
        n.src="https://bat.bing.net/bat.js?ti="+o.ti+("uetq"!=u?"&q="+u:""),n.async=1,
        n.onload=n.onreadystatechange=function(){var s=this.readyState;s&&"loaded"!==s&&"complete"!==s||(
        o.q=w[u],w[u]=new UET(o),w[u].push("pageLoad"),n.onload=n.onreadystatechange=null)};
        var i=d.getElementsByTagName(t)[0];i.parentNode.insertBefore(n,i);
        })(window,document,"script","uetq",{ti:"97241171", enableAutoSpaTracking: true});
  `;
    document.body.appendChild(bingUETEl);
  }

  private initializeOpenAiPixel() {
    const pixelId = environment.openAiPixelId;
    if (!pixelId) {
      return;
    }

    const openAiPixelEl = document.createElement('script');
    openAiPixelEl.type = 'text/javascript';
    openAiPixelEl.innerHTML = `
        (function (w, d, s, u) {
          if (w.oaiq) return;
          var q = function () { q.q.push(arguments); };
          q.q = [];
          w.oaiq = q;
          var js = d.createElement(s);
          js.async = true;
          js.src = u;
          var f = d.getElementsByTagName(s)[0];
          f.parentNode.insertBefore(js, f);
        })(window, document, "script", "https://bzrcdn.openai.com/sdk/oaiq.min.js");
        oaiq("init", { pixelId: "${pixelId}", debug: true });
        oaiq("measure", "page_viewed", { type: "contents" });
  `;
    document.body.appendChild(openAiPixelEl);
  }

  initializeBingIdSync() {
    try {
      const customerId = environment.bingCustomerId || '';
      const vid = this.userExternalId;

      if (!customerId || !vid) {
        return;
      }

      const throttleKey = `bingIdSyncFired:${vid}`;
      if (sessionStorage.getItem(throttleKey)) {
        return;
      }

      const authenticatedUid = this.user?.id || '';
      const params = `vid=${encodeURIComponent(vid)}&Red3=BACID_${encodeURIComponent(customerId)}`;
      const uidParam = authenticatedUid ? `&uid=${encodeURIComponent(authenticatedUid)}` : '';

      const bingIdSyncEl = document.createElement('img');
      bingIdSyncEl.src = `https://c.bing.com/c.gif?${params}${uidParam}`;
      bingIdSyncEl.width = 0;
      bingIdSyncEl.height = 0;
      bingIdSyncEl.style.display = 'none';
      bingIdSyncEl.alt = '';

      document.body.appendChild(bingIdSyncEl);
      sessionStorage.setItem(throttleKey, '1');
    } catch (error) {
      console.warn('Bing ID sync error:', error);
    }
  }

  initializeToltReferral() {
    const tolReferralEl = document.createElement('script');
    tolReferralEl.type = 'text/javascript';
    tolReferralEl.async = true;
    tolReferralEl.setAttribute('src', 'https://cdn.tolt.io/tolt.js');
    tolReferralEl.setAttribute('data-tolt', 'b1fde686-cdf1-4a24-8410-ebae42dc1f49');

    tolReferralEl.onload = () => {
      setTimeout(() => {
        if (window['tolt_referral']) {
          localStorage.setItem('tolt_referral', window['tolt_referral']);
        }
      }, 2500);
    };

    document.body.appendChild(tolReferralEl);
  }

  initializeRewardful() {
    const rewardfulSnippetEl = document.createElement('script');
    rewardfulSnippetEl.type = 'text/javascript';
    rewardfulSnippetEl.innerHTML = `
      (function(w,r){w._rwq=r;w[r]=w[r]||function(){(w[r].q=w[r].q||[]).push(arguments)}})(window,'rewardful');
    `;
    document.head.appendChild(rewardfulSnippetEl);

    const rewardfulEl = document.createElement('script');
    rewardfulEl.type = 'text/javascript';
    rewardfulEl.async = true;
    rewardfulEl.setAttribute('src', 'https://r.wdfl.co/rw.js');
    rewardfulEl.setAttribute('data-rewardful', environment.rewardfulApiKey);

    rewardfulEl.onload = () => {
      if (typeof window.rewardful === 'function') {
        window.rewardful('ready', () => {
          try {
            if (window.Rewardful?.referral) {
              localStorage.setItem('rewardful_referral_id', window.Rewardful.referral);
            }
          } catch (error) {
            console.warn('Error storing Rewardful data:', error);
          }
        });
      }
    };

    document.head.appendChild(rewardfulEl);
  }

  private initializeClarity() {
    try {
      const clarityEl = document.createElement('script');
      clarityEl.type = 'text/javascript';
      clarityEl.innerHTML = `
        (function(c,l,a,r,i,t,y){
          c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};
          t=l.createElement(r);t.async=1;t.src="https://www.clarity.ms/tag/"+i;
          y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y);
        })(window, document, "clarity", "script", "w17iyowmsl");
      `;
      document.body.appendChild(clarityEl);
    } catch (error) {
      console.error('Failed to initialize Clarity:', error);
    }
  }

  private identifyClarity() {
    try {
      if (window.clarity) {
        window.clarity('identify', this.userId);
        const businessId = this.user.activeBusiness?.id;
        if (businessId) {
          window.clarity('set', 'business_id', String(businessId));
        }
      }
    } catch (error) {
      console.error('Failed to identify Clarity user:', error);
    }
  }

  setExternalUserUuid(userUuid: string) {
    if (userUuid) {
      localStorage.setItem('userExternalId', userUuid);
      if (this.isProductionEnv) {
        this.initializeBingIdSync();
      }
    }
  }

  setLeadUuid(leadUuid: string) {
    if (leadUuid) {
      localStorage.setItem('leadUuid', leadUuid);
    }
  }


  private handleFacebookPixelPageView(pageTitle: AnalyticsPage) {
    if (this.isOnboardingPage(pageTitle)) {
      const eventId = this.generateEventId(this.userId, 'ViewContent');
      fbq('track', 'ViewContent', {
        ...(eventId && { event_id: eventId })
      });
    }

    if (pageTitle === AnalyticsPage.OnbordingAgentPreview) {
      const eventId = this.generateLeadEventId();
      fbq('track', 'Lead', {
        ...(eventId && { event_id: eventId })
      });
      uetq.push('event', 'lead', {
        event_category: 'onboarding'
      });
    }
  }

  private handleTikTokPageView(pageTitle: AnalyticsPage) {
    if (this.isOnboardingPage(pageTitle)) {
      ttq.track('ViewContent', {
        "contents": [
          {
            "content_id": "rosie",
            "content_type": "product",
            "content_name": "package"
          }
        ]
      });
    }
  }

  private handleGAPageView(pageTitle: AnalyticsPage) {
    if (this.isOnboardingPage(pageTitle)) {
      gtag('event', 'page_view', { 'page_title': pageTitle, send_to: ['AW-11564387623', 'G-8XN13HV4CR'] });
    }
    if (pageTitle === AnalyticsPage.OnboardingUserRegistration) {
      gtag("event", "generate_lead", {
        send_to: ['AW-11564387623', 'G-8XN13HV4CR'],
        lead_source: "Landing Page",
        items: [
          {
            item_id: "rosie",
          }
        ]
      });
    }
  }

  private handleOpenAiPageView(pageTitle: AnalyticsPage) {
    if (this.isOnboardingPage(pageTitle)) {
      this.fireOpenAi('contents_viewed', {
        type: 'contents',
        contents: [{ id: pageTitle, name: pageTitle, content_type: 'page' }]
      });
    }

    if (pageTitle === AnalyticsPage.OnbordingAgentPreview) {
      const eventId = this.generateEventId(this.getStoredLeadUuid(), 'lead_created');
      this.fireOpenAi('lead_created', { type: 'customer_action' }, {
        ...(eventId && { event_id: eventId })
      });
    }
  }

  private fireOpenAi(eventName: string, eventProps: any = {}, eventOptions: any = {}) {
    try {
      if (this.isProductionEnv && typeof oaiq !== 'undefined' && environment.openAiPixelId) {
        oaiq('measure', eventName, eventProps, eventOptions);
      }
    } catch (error) {
      console.warn('OpenAI pixel tracking error:', error);
    }
  }

  private handleMeta() {
    const queryParams = new URLSearchParams(window.location.search);
    const coupon = queryParams.get('coupon');
    const userUuid = queryParams.get('uuid');
    const referral = queryParams.get('rrf');
    const campaign = queryParams.get('rcmp');
    const toltReferral = queryParams.get('toltReferral');
    const rewardfulReferral = queryParams.get('referral');
    const utmSource = queryParams.get('utm_source');
    const utmMedium = queryParams.get('utm_medium');
    const utmCampaign = queryParams.get('utm_campaign');
    const utmTerm = queryParams.get('utm_term');
    const utmContent = queryParams.get('utm_content');
    const referrer = queryParams.get('referrer');
    const msclkid = queryParams.get('msclkid');
    const oppref = queryParams.get('oppref');
    const secondaryReferrer = document.referrer;
    if (coupon) {
      localStorage.setItem('signupCouponCode', coupon);
    }

    if (referral && referral !== 'null') {
      localStorage.setItem('rrf', referral);
    }

    if (campaign && campaign !== 'null') {
      localStorage.setItem('rcmp', campaign);
    }

    if (toltReferral) {
      localStorage.setItem('tolt_referral', toltReferral);
    }

    if (rewardfulReferral) {
      localStorage.setItem('rewardful_referral_id', rewardfulReferral);
    }

    if (utmSource && utmSource !== 'null') {
      localStorage.setItem('utm_source', utmSource);
    }

    if (utmMedium && utmMedium !== 'null') {
      localStorage.setItem('utm_medium', utmMedium);
    }

    if (utmCampaign && utmCampaign !== 'null') {
      localStorage.setItem('utm_campaign', utmCampaign);
    }

    if (utmTerm && utmTerm !== 'null') {
      localStorage.setItem('utm_term', utmTerm);
    }

    if (utmContent && utmContent !== 'null') {
      localStorage.setItem('utm_content', utmContent);
    }

    if (referrer && referrer !== 'null') {
      localStorage.setItem('referrer', referrer);
    }

    if (msclkid && msclkid !== 'null') {
      localStorage.setItem('msclkid', msclkid);
    }

    if (oppref && oppref !== 'null') {
      localStorage.setItem('oppref', oppref);
    }

    if (secondaryReferrer && !this.secondaryReferrer) {
      localStorage.setItem('secondary_referrer', secondaryReferrer);
    }

    this.setExternalUserUuid(userUuid);
  }

  private isOnboardingPage(pageTitle: AnalyticsPage): boolean {
    const onboardingPages = [
      AnalyticsPage.OnboardingGoogleProfile,
      AnalyticsPage.OnboardingInfoManual,
      AnalyticsPage.OnboardingInfoWebsite,
      AnalyticsPage.OnboardingAgentCreation,
      AnalyticsPage.OnbordingAgentPreview,
      AnalyticsPage.OnboardingUserRegistration
    ];
    return onboardingPages.includes(pageTitle);
  }

  // Variant D registers without a lead, so the pre-account flow that writes
  // campaign/coupon/referral/UTM onto the lead (see AgentComponent.handleLeadMeta)
  // never runs. Build the same bundle to send directly on the register request —
  // the registration controller reads each field request-first when there is no
  // lead, so attribution and coupons survive the skipped flow. Mirror the lead
  // shape exactly (the HTTP interceptor snake_cases the keys on the way out).
  async getRegistrationAttribution(): Promise<Record<string, any>> {
    const attribution: Record<string, any> = {};

    if (this.coupon) { attribution.couponCode = this.coupon; }
    if (this.toltReferral) { attribution.toltReferral = this.toltReferral; }
    if (this.rewardfulReferralId) { attribution.rewardfulReferral = this.rewardfulReferralId; }
    if (this.userExternalId) { attribution.userUuid = this.userExternalId; }

    if (this.referral) {
      attribution.campaign = { rrf: this.referral, rcmp: this.campaign };
    }

    const utm: Record<string, string> = {};
    if (this.utmSource) { utm.source = this.utmSource; }
    if (this.utmMedium) { utm.medium = this.utmMedium; }
    if (this.utmCampaign) { utm.campaign = this.utmCampaign; }
    if (this.utmTerm) { utm.term = this.utmTerm; }
    if (this.utmContent) { utm.content = this.utmContent; }
    if (this.referrer) { utm.referrer = this.referrer; }
    if (this.secondaryReferrer) { utm.secondaryReferrer = this.secondaryReferrer; }

    if (Object.keys(utm).length > 0) { attribution.utm = utm; }

    const facebook = this.getFacebookUserData();
    if (facebook.fbc) { attribution.fbc = facebook.fbc; }
    if (facebook.fbp) { attribution.fbp = facebook.fbp; }

    if (this.msclkid) { attribution.msclkid = this.msclkid; }
    if (this.oppref) { attribution.oppref = this.oppref; }

    const google = await this.getGoogleUserData();
    if (google.clientId) { attribution.googleClientId = google.clientId; }
    if (google.sessionId) { attribution.googleSessionId = google.sessionId; }

    return attribution;
  }

  getFacebookUserData(): any {
    try {
      const cookieStorage = new CookieStorage();
      const userData: any = {};

      const fbc = cookieStorage.getItem('_fbc');
      const fbp = cookieStorage.getItem('_fbp');

      if (fbc) userData.fbc = fbc;
      if (fbp) userData.fbp = fbp;

      return userData;
    } catch (error) {
      return {};
    }
  }

  getGoogleUserData(): Promise<{ clientId?: string; sessionId?: string }> {
    return new Promise((resolve) => {
      const userData: { clientId?: string; sessionId?: string } = {};

      // Extract session_id from cookie (supports GS1 and GS2 formats)
      const cookieStorage = new CookieStorage();
      const sessionCookie = cookieStorage.getItem('_ga_8XN13HV4CR');
      if (sessionCookie) {
        if (sessionCookie.startsWith('GS2')) {
          const match = sessionCookie.match(/\.s(\d+)\$/);
          if (match) userData.sessionId = match[1];
        } else {
          const parts = sessionCookie.split('.');
          if (parts.length >= 3) userData.sessionId = parts[2];
        }
      }

      // Use gtag API for client_id
      if (typeof gtag === 'function') {
        gtag('get', 'G-8XN13HV4CR', 'client_id', (clientId: string) => {
          if (clientId) userData.clientId = clientId;
          resolve(userData);
        });
        setTimeout(() => resolve(userData), 1000);
      } else {
        resolve(userData);
      }
    });
  }

  private generateEventId(uuid: string, eventName: string): string {
    if (!uuid) return '';
    const input = `${uuid}_${eventName}`;
    return CryptoJS.MD5(input).toString();
  }

  private generateCompleteRegistrationEventId(): string {
    const userUuid = this.user?.id || '';
    return this.generateEventId(userUuid, 'CompleteRegistration');
  }

  private generateLeadEventId(leadUuid?: string): string {
    const uuid = leadUuid || this.getStoredLeadUuid();
    return this.generateEventId(uuid, 'Lead');
  }

  private getStoredLeadUuid(): string {
    const leadUuid = localStorage.getItem('leadUuid');
    if (leadUuid) {
      return leadUuid;
    }

    const storedLead = localStorage.getItem('lead');
    if (storedLead) {
      try {
        const lead = JSON.parse(storedLead);
        return lead.id || lead.userUuid || '';
      } catch (error) {
        console.warn('Error parsing stored lead:', error);
      }
    }
    return this.userExternalId;
  }
}

export enum AnalyticsEvent {
  AppInstalled = 'app_installed',

  NavigationItemAction = 'navigation_item_clicked',

  CallStarred = 'call_starred',
  CallUnstarred = 'call_unstarred',
  CallViewed = 'call_viewed',
  CallArchived = 'call_archived',
  CallListenToRecording = 'call_listen_to_recording',
  CallRated = 'call_rated',
  ConversationsBulkAction = 'conversations_bulk_action',

  ClearRecordings = 'clear_recordings',

  CardAdded = 'card_added',
  CardAddedError = 'card_added_error',
  SubscriptionPlanChanged = 'subscription_plan_changed',
  UpgradeToEnablePremiumAction = 'upgrade_to_enable_premium_clicked',
  AddCreditCartAlertInitiated = 'add_credit_cart_alert_initiated',

  GooglePlacesBusinessSearched = 'google_places_business_searched',
  GooglePlacesBusinessSelected = 'google_places_business_selected',
  GooglePlacesBusinessActionContinue = 'google_places_business_clicked_continue',
  GooglePlacesBusinessSearchAgain = 'google_places_business_search_again',
  GooglePlacesBusinessTrainAction = 'google_places_business_train_action',

  GooglePlacesAddressSearched = 'google_places_address_searched',
  GooglePlacesAddressSelected = 'google_places_address_selected',

  WebsiteUrlTrainingSelected = 'website_url_training_selected',
  WebsiteUrlEntered = 'website_url_entered',
  WensiteUrlTrainAction = 'website_url_train_action',

  ManualBusinessInfoSelected = 'manual_business_info_selected',
  ManualBusinessInfoEntered = 'manual_business_info_entered',
  ManualBusinessInfoTrainAction = 'manual_business_info_train_action',

  AgentBuildStarted = 'agent_build_started',
  AgentPreviewReady = 'agent_preview_ready',
  AgentPreviewClipAction = 'agent_preview_clip_clicked',
  AgentPreviewClaimAction = 'agent_preview_claim_action',


  //onboarding
  GoogleSSORegisterAction = 'google_sso_register_action',
  GoogleSSORegister = 'google_sso_register',
  UserInfoEntered = 'user_info_entered',
  UserFormRegisterAction = 'form_register_action',
  UserFormRegister = 'form_register',
  UserRegisteredAccount = 'user_registered_account',

  OnboardingStepViewed = 'onboarding_step_viewed',

  SourcesEditAction = 'sources_edit_clicked',
  SourcesWebsiteUrlEdited = 'sources_website_url_edited',
  SourcesGoogleProfileEdited = 'sources_google_profile_edited',
  SourcesInitialTrainAction = 'sources_initial_train_action',
  SourcesTrainActionCompleted = 'sources_train_action_completed',
  SourcesUseWebsiteUrl = 'sources_use_website_url',
  SourcesUseGoogleProfile = 'sources_use_google_profile',
  SourcesRetrainAction = 'sources_retrain_action',
  SourcesCancelAction = 'sources_cancel_action',

  OnboardingBusinessNameEdited = 'onboarding_business_name_edited',
  OnboardingBusinessPhoneNumberChanged = 'onboarding_business_phone_number_changed',
  OnboardingBusinessOverviewEdited = 'onboarding_business_overview_edited',
  OnboardingBusinessServiceAdded = 'onboarding_business_services_changed',
  OnboardingBusinessServiceRemoved = 'onboarding_business_service_removed',
  OnboardingQuestionAdded = 'onboarding_question_added',
  OnboardingQuestionRemoved = 'onboarding_question_removed',
  OnboardingFaqAdded = 'onboarding_faq_added',
  OnboardingFaqRemoved = 'onboarding_faq_removed',
  OnboardingEmailNotificationChanged = 'onboarding_email_notification_changed',
  OnboardingTextNotificationChanged = 'onboarding_text_notification_changed',
  OnboardingTextNotificationNumberChanged = 'onboarding_text_notification_number_changed',
  OnboardingGreetingDisclaimerChanged = 'onboarding_greeting_disclaimer_toggled',
  OnboardingGreetingChanged = 'onboarding_greeting_changed',

  OnboardingCallRosiePhone = 'onboarding_call_rosie_phone',
  OnboardingContinueAction = 'onboarding_continue_action',

  OnboardingCardSaveChangesAction = 'onboarding_card_save_changes_action',
  OnboardingCardCancelAction = 'onboarding_card_cancel_action',
  OnboardingCardMakeChangesAction = 'onboarding_card_make_changes_action',
  OnboardingCardContinueAction = 'onboarding_card_continue_action',
  OnboardingCardPreviousAction = 'onboarding_card_previous_action',
  OnboardingCardViewed = 'onboarding_card_viewed',

  OnboardingAppointmentsChanged = 'onboarding_premium_appointments_toggled',
  OnboardingDirectSchedulingChanged = 'onboarding_premium_direct_scheduling_toggled',
  OnboardingTextLinksChanged = 'onboarding_premium_text_links_toggled',
  OnboardingTransferCallsChanged = 'onboarding_premium_transfer_calls_toggled',

  OnboardingProgressBarAction = 'onboarding_progress_bar_action',

  OnboardingLaunchViewInstructions = 'onboarding_launch_view_instructions',
  OnboardingLaunchCheckedNumberIsForwarding = 'onboarding_launch_checked_number_is_forwarding',
  OnboardingLaunchUncheckedNumberIsForwarding = 'onboarding_launch_unchecked_number_is_forwarding',
  OnboardingLaunchNeedsHelpAction = 'onboarding_launch_needs_help_action',
  OnboardingLaunchAddCreditCardAction = 'onboarding_launch_add_credit_card_action',
  OnboardingFaqsAction = 'onboarding_launch_faqs_action',
  OnboardingNotificationAction = 'onboarding_launch_notification_action',
  OnboardingCallsAction = 'onboarding_launch_calls_action',
  OnboardingPhoneSystemTypeSelected = 'onboarding_phone_system_type_selected',
  OnboardingPhoneProviderSelected = 'onboarding_phone_provider_selected',
  OnboardingUseRosieNumberSelected = 'onboarding_use_rosie_number_selected',

  OnboardingVideoTourPlayed = 'onboarding_video_tour_played',

  //onboarding end




  CustomGreetingChanged = 'custom_greeting_changed',
  CustomGreetingEnabled = 'custom_greeting_enabled',
  CustomGreetingPreview = 'custom_greeting_preview',
  CustomGreetingDisabled = 'custom_greeting_disabled',
  LegalDisclaimerEnabled = 'legal_disclaimer_enabled',
  LegalDisclaimerDisabled = 'legal_disclaimer_disabled',

  NotificationEmailAdded = 'notification_email_added',
  NotificationEmailRemoved = 'notification_email_removed',
  NotificationPhoneNumberAdded = 'notification_phone_number_added',
  NotificationPhoneNumberRemoved = 'notification_phone_number_removed',

  BusinessInformationTitleEdited = 'business_information_title_edited',
  BusinessInformationSummaryEdited = 'business_information_summary_edited',
  BusinessInformationAddressChanged = 'business_information_address_changed',
  BusinessInformationTimezoneChanged = 'business_information_timezone_changed',
  BusinessPhoneNumberChanged = 'business_phone_number_changed',

  AgentProfileToneChanged = 'agent_profile_tone_changed',
  AgentProfileTitleChanged = 'agent_profile_title_changed',
  AgentBackgroundNoiseChanged = 'agent_background_noise_changed',

  AppointmentsEnabled = 'appointments_enabled',
  AppointmentsDisabled = 'appointments_disabled',
  AppointmentsMessageChanged = 'appointments_message_changed',
  AppointmentsUrlChanged = 'appointments_url_changed',

  AppointmentSchedulingEnabled = 'appointment_scheduling_enabled',
  AppointmentSchedulingDisabled = 'appointment_scheduling_disabled',

  SpamFilterSmartDetectionEnabled = 'spam_filter_smart_detection_enabled',
  SpamFilterSmartDetectionDisabled = 'spam_filter_smart_detection_disabled',
  SpamFilterBlock1800NumbersEnabled = 'spam_filter_block_1800_numbers_enabled',
  SpamFilterBlock1800NumbersDisabled = 'spam_filter_block_1800_numbers_disabled',

  TrainingFaqsChanged = 'training_faqs_changed',

  TrainingFilesUploaded = 'training_files_uploaded',
  TrainingFilesDeleted = 'training_files_deleted',

  TransferCallsChanged = 'transfer_calls_changed',

  TakeAMessageChanged = 'take_a_message_changed',

  TextALinkChanged = 'text_a_link_changed',

  PaymentFailedBannerAction = 'payment_failed_banner_clicked',
  ResubscribeBannerAction = 'resubscribe_banner_clicked',

  UserInvited = 'user_invited',
  UserRemoved = 'user_removed',
  UserAcceptedBusinessInvitation = 'user_accepted_business_invitation',

  SettingsLaunchCheckedNumberIsForwarding = 'settings_launch_checked_number_is_forwarding',
  SettingsLaunchUncheckedNumberIsForwarding = 'settings_launch_unchecked_number_is_forwarding',
  SettingsLaunchViewInstructions = 'settings_launch_view_instructions',
  SettingsLaunchNeedsHelpAction = 'settings_launch_needs_help_action',
  SettingsLaunchAddCreditCardAction = 'settings_launch_add_credit_card_action',
  SettingsCallRosiePhone = 'settings_call_rosie_phone',
  SettingsPhoneSystemTypeSelected = 'settings_phone_system_type_selected',
  SettingsPhoneProviderSelected = 'settings_phone_provider_selected',
  SettingsUseRosieNumberSelected = 'settings_use_rosie_number_selected',
  RequestLocalAreaCodeClicked = 'request_local_area_code_clicked',


  AccountSwitched = 'business_account_switched',
  AccountSearched = 'business_account_searched',
  AccountCreated = 'business_account_created',
}

export enum AnalyticsPage {

  OnboardingUserRegistration = 'account_creation_page',
  OnboardingTrain = 'onboarding_train_page',
  OnboardingCustomize = 'onboarding_customize_page',
  OnboardingTest = 'onboarding_test_page',
  OnboardingLaunch = 'onboarding_launch_page',

  ConfigurationBusinessInformation = 'configuration_business_information_page',
  ConfigurationAgentProfile = 'configuration_agent_profile_page',
  ConfigurationGreeting = 'configuration_greeting_page',
  ConfigurationTakeAMessage = 'configuration_take_a_message_page',
  ConfigurationAppointments = 'configuration_appointments_page',
  ConfigurationSpamFilter = 'configuration_spam_filter_page',
  ConfigurationTrainingFaqs = 'configuration_training_faqs_page',
  ConfigurationTrainingFiles = 'configuration_training_files_page',
  ConfigurationTransferCalls = 'configuration_transfer_calls_page',
  ConfigurationTextLinks = 'configuration_text_links_page',
  ConfigurationLaunch = 'configuration_launch_page',
  ConfigurationSettings = 'configuration_settings_page',

  AccountBillingPage = 'account_billing_page',
  AccountNotifications = 'account_notifications_page',
  AccountProfile = 'account_settings_page',
  AccountUsers = 'account_users_page',
  Account = 'account_page',

  Login = 'login_page',
  ForgotPassword = 'forgot_password_page',
  ResetPassword = 'reset_password_page',
  NotFound = 'not_found_page',
  Calls = 'calls_page',
  Integrations = 'integrations_page',
  AcceptBusinessInvite = 'accept_business_invite_page',


  OnboardingGoogleProfile = 'onboarding_google_profile_page', // deprecated for now
  OnboardingInfoManual = 'onboarding_info_manual_page',
  OnboardingInfoWebsite = 'onboarding_info_website_page',
  OnboardingAgentCreation = 'onboarding_agent_creation_page',
  OnbordingAgentPreview = 'onboarding_agent_preview_page',
}
